import type {
  IArticle,
  IAuthor,
  IJournal,
  IResearchTopic,
  IRtSponsorship
} from "~/types";
import { SearchDataModel } from "~/types/models/SearchData.model";
import type { IArticleSchema } from "~/types/models/Article.model";
import type { IArticle2025Schema } from "~/types/models/Article2025.model";
import { ArticleSortOptionEnum } from "~/enums/ArticleSortOptionEnum";
import type { Sort } from "mongodb";
import type { IAuthorSchema } from "~/types/models/Author.model";
import type {
  IResearchTopicBasicInfoSchema,
  IResearchTopicSchema
} from "~/types/models/ResearchTopic.model";
import type {
  IResearchTopicBasicInfo2025Schema,
  IResearchTopic2025Schema
} from "~/types/models/ResearchTopic2025.model";
import {
  ResearchTopicSchema2Interface,
  RTSponsorshipSchema2Interface,
  mapResearchTopic2025ToLegacy,
  mapResearchTopicBasicInfo2025ToLegacy,
  mapArticle2025ToLegacy,
  mapArticleAuthor2025ToLegacy
} from "~/mappers";
import {
  getArticleProjection,
  getMagazineArticleProjection,
  getAuthorProjection,
  getResearchTopicEbookProjection,
  getResearchTopicProjection
} from "~/helpers/mongoProjections";
import type { IRTSponsorshipSchema } from "~/types/models/RTSponsorship.model";
import type { IJournalSchema } from "~/types/models/Journal.model";
import type { IListResponse } from "~/types/IListResponse";
import { useMongoDbConnectionManager } from "./useMongoDbConnectionManager";
import type {
  IResearchTopicEbookSchema,
  IResearchTopicEbookSchema2025
} from "~/types/models/ResearchTopicEbook.model";
import { mapIArticleSchemaToIArticle } from "~/mappers/ArticleSchemaToInterface";
import { hasMagazineArticleTitle } from "~/helpers/magazineHelpers";
import { useAuthorMappers } from "./useAuthorMappers";

export const useMongoDbService = () => {
  const { mapIAuthorSchemaToIAuthor, getUniqueAuthors } = useAuthorMappers();

  function getQueryFilters(
    rtId: number,
    options?: { requireTitle?: boolean }
  ): object[] {
    const stageIds = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17];
    const filter: object[] = [
      { "ResearchTopic.ResearchTopicId": rtId },
      {
        PublishedDate: {
          $lt: new Date()
        }
      },
      { Authors: { $exists: true, $ne: [] } }
    ];

    if (options?.requireTitle) {
      filter.push({
        Title: { $exists: true, $type: "string", $regex: /\S/ }
      });
    }

    if (excludedStageIds.length > 0) {
      filter.push({
        StageId: { $nin: excludedStageIds.split(",") }
      });
    }
    if (publiclyVisibleStages.length > 0) {
      filter.push({
        StageId: { $in: publiclyVisibleStages.split(",") }
      });
    }
    if (excludedJournalIds.length > 0) {
      filter.push({
        $or: [
          {
            "Journal.JournalId": {
              $nin: excludedJournalIds.split(",")
            }
          },
          { StageId: { $nin: stageIds } }
        ]
      });
    }
    return filter;
  }

  function removeOfflineJournalsAndSectionsWithoutArticles(
    journals: IJournal[],
    allArticles: IArticle[]
  ): IJournal[] {
    return journals.filter((journal) => {
      if (journal.isPrimary) {
        if (journal.sections?.length) {
          journal.sections = journal.sections.filter((section) => {
            if (section.isOnline) return true;
            return allArticles.some(
              (article) =>
                article.journal?.journalId === journal.id &&
                article.section?.sectionId === section.sectionId
            );
          });
        }
        return true;
      }

      if (journal.specialtyId) {
        if (journal.isOnline) return true;

        return allArticles.some(
          (article) => article.journal?.journalId === journal.id
        );
      } else if (journal.sections?.length) {
        journal.sections = journal.sections.filter((section) => {
          if (section.isOnline) return true;

          return allArticles.some(
            (article) =>
              article.journal?.journalId === journal.id &&
              article.section?.sectionId === section.sectionId
          );
        });

        if (journal.isOnline && journal.sections.length > 0) return true;

        return allArticles.some(
          (article) => article.journal?.journalId === journal.id
        );
      }

      return false;
    });
  }

  async function getJournals(
    journalIds: number[],
    sectionIds: number[]
  ): Promise<IJournalSchema[]> {
    const { collection, dataSource } = await getCollectionWithMetadata("journals");
    const filteredJournals = await collection
      .aggregate([
        {
          $match: {
            JournalId: { $in: journalIds }
          }
        },
        {
          $project: {
            ArticleTypes: true,
            ImpactMetrics: true,
            JournalId: true,
            Online: true,
            // Replicator2025 uses IsOpenForSubmission; alias to legacy field name
            OpenForSubmission: dataSource === "replicator2025" ? "$IsOpenForSubmission" : true,
            Slug: true,
            Title: true,
            Taxonomies: true,
            Sections: {
              $map: {
                input: {
                  $filter: {
                    input: "$Sections",
                    as: "section",
                    cond: { $in: ["$$section.SectionId", sectionIds] }
                  }
                },
                as: "filteredSection",
                in: {
                  SectionId: "$$filteredSection.SectionId",
                  Title: "$$filteredSection.Title",
                  Online: "$$filteredSection.Online",
                  Slug: "$$filteredSection.Slug",
                  OpenForSubmission: "$$filteredSection.OpenForSubmission",
                  ArticleTypes: "$$filteredSection.ArticleTypes",
                  Taxonomies: "$$filteredSection.Taxonomies"
                }
              }
            }
          }
        }
      ])
      .toArray();
    return filteredJournals as IJournalSchema[];
  }

  const { excludedStageIds, publiclyVisibleStages, excludedJournalIds } =
    useRuntimeConfig().public;
  const { getCollectionWithMetadata } = useMongoDbConnectionManager();

  const existsResearchTopic = async (
    researchTopicId: number
  ): Promise<boolean> => {
    const rt = await getResearchTopicBasicInfo(researchTopicId);
    return rt !== null;
  };

  const getArticles = async (
    researchTopicId: number,
    params: SearchDataModel = new SearchDataModel()
  ): Promise<IArticle[]> => {
    const {
      limit,
      skip,
      sortby,
      applyMagazineFilters,
      useMagazineProjection
    } = params;

    const { collection, dataSource } =
      await getCollectionWithMetadata("articles");

    const projection = useMagazineProjection
      ? getMagazineArticleProjection(dataSource)
      : getArticleProjection(dataSource);

    // Determine sort criteria
    let mongoSortBy!: Sort;

    switch (sortby) {
      case ArticleSortOptionEnum.VIEWS:
        mongoSortBy = { "ImpactV5.Views": -1, "_id": 1 };
        break;
      case ArticleSortOptionEnum.ARTICLETYPE:
        mongoSortBy = { "ArticleType.Name": 1, "_id": 1 };
        break;
      case ArticleSortOptionEnum.DATE:
        mongoSortBy =
          dataSource === "replicator2025"
            ? { PublishedDate: -1, _id: 1 }
            : { RecentDate: -1, _id: 1 };
        break;
      case ArticleSortOptionEnum.SCORE:
        mongoSortBy =
          dataSource === "replicator2025"
            ? { Score: -1, _id: 1 }
            : { "ImpactV5.Views": -1, _id: 1 };
        break;
      default:
        throw Error("Unhandled sort by param: " + sortby);
    }

    const filter = applyMagazineFilters
      ? { $and: getQueryFilters(researchTopicId, { requireTitle: true }) }
      : { "ResearchTopic.ResearchTopicId": researchTopicId };

    const filterArticlesForMagazine = (items: IArticle[]) =>
      applyMagazineFilters ? items.filter(hasMagazineArticleTitle) : items;

    if (dataSource === "replicator2025") {
      // Score may be missing on older docs; treat as 0 so sort order is stable
      if (sortby === ArticleSortOptionEnum.SCORE) {
        const articles2025 = await collection
          .aggregate<IArticle2025Schema>([
            { $match: filter },
            { $addFields: { Score: { $ifNull: ["$Score", 0] } } },
            { $sort: { Score: -1, _id: 1 } },
            { $skip: skip },
            { $limit: limit },
            { $project: projection }
          ])
          .toArray();

        return filterArticlesForMagazine(
          articles2025.map((a) =>
            mapIArticleSchemaToIArticle(mapArticle2025ToLegacy(a))
          )
        );
      }

      const articles2025 = await collection
        .find<IArticle2025Schema>(filter, {
          projection
        })
        .skip(skip)
        .limit(limit)
        .sort(mongoSortBy)
        .toArray();

      return filterArticlesForMagazine(
        articles2025.map((a) =>
          mapIArticleSchemaToIArticle(mapArticle2025ToLegacy(a))
        )
      );
    } else {
      const articleListDocument = await collection
        .find<IArticleSchema>(filter, {
          projection
        })
        .skip(skip)
        .limit(limit)
        .sort(mongoSortBy)
        .toArray();

      return filterArticlesForMagazine(
        articleListDocument.map((a) => mapIArticleSchemaToIArticle(a))
      );
    }
  };

  const getAuthors = async (
    researchTopicId: number,
    params: SearchDataModel
  ): Promise<IListResponse<IAuthor>> => {
    const filter: object[] = getQueryFilters(researchTopicId);

    const { collection, dataSource } =
      await getCollectionWithMetadata("articles");

    const authors: IAuthor[] = [];
    const users: IAuthorSchema[] = [];
    if (dataSource === "replicator2025") {
      const articles2025 = await collection
        .find<IArticle2025Schema>(
          { $and: filter },
          { projection: getAuthorProjection(dataSource) }
        )
        .toArray();
      articles2025.forEach((article) => {
        if (article.Authors && Array.isArray(article.Authors)) {
          const legacyAuthors = article.Authors.map(
            mapArticleAuthor2025ToLegacy
          );
          users.push(...legacyAuthors);
        }
      });
    } else {
      const articleListDocument = await collection
        .find<IArticleSchema>(
          { $and: filter },
          { projection: getAuthorProjection(dataSource) }
        )
        .toArray();

      articleListDocument.forEach((article) => users.push(...article.Authors));
    }
    const uniqueAuthors = getUniqueAuthors(users);
    authors.push(
      ...uniqueAuthors.map((author) => mapIAuthorSchemaToIAuthor(author))
    );

    const totalAuthors = authors.length;
    let paginatedAndSortedAuthors: IAuthor[] = [];
    if (totalAuthors > 0) {
      paginatedAndSortedAuthors = authors
        .sort((a, b) => {
          if (a.userId > 0 && b.userId > 0)
            return a.fullName.localeCompare(b.fullName);
          if (a.userId > 0) return -1;
          if (b.userId > 0) return 1;
          return a.fullName.localeCompare(b.fullName);
        })
        .slice(params.skip, params.skip + params.limit);
    }

    return {
      total: totalAuthors,
      items: paginatedAndSortedAuthors,
      offset: params.skip,
      limit: params.limit
    } as IListResponse<IAuthor>;
  };

  const getEbooks = async (
    researchTopicId: number
  ): Promise<IResearchTopicEbookSchema[]> => {
    const { collection, dataSource } = await getCollectionWithMetadata(
      "researchTopicsEbooks"
    );

    if (dataSource === "replicator2025") {
      const ebook2025 = await collection.findOne<IResearchTopicEbookSchema2025>(
        { ResearchTopicId: researchTopicId },
        { projection: getResearchTopicEbookProjection(dataSource) }
      );

      return ebook2025
        ? ebook2025.FileDetails.map((fileDetail) => ({
            ResearchTopicId: ebook2025.ResearchTopicId,
            EbookId: fileDetail.EbookId,
            FileServerVersionNumber: fileDetail.FileServerVersionNumber,
            FileServerId: fileDetail.FileServerId,
            FileServerPackageEntryId: fileDetail.FileServerPackageEntryId,
            FileName: fileDetail.FileName,
            ContentTypeCode: fileDetail.ContentTypeCode,
            FileFormatDescription: fileDetail.FileFormatDescription
          }))
        : [];
    }

    return collection
      .find<IResearchTopicEbookSchema>(
        { ResearchTopicId: researchTopicId },
        { projection: getResearchTopicEbookProjection(dataSource) }
      )
      .toArray();
  };

  const getResearchTopicBasicInfo = async (
    researchTopicId: number
  ): Promise<IResearchTopicBasicInfoSchema | null> => {
    const { collection, dataSource } =
      await getCollectionWithMetadata("researchTopics");

    let rt: IResearchTopicBasicInfoSchema | null = null;

    if (dataSource === "replicator2025") {
      const rt2025 =
        await collection.findOne<IResearchTopicBasicInfo2025Schema>(
          { ResearchTopicId: researchTopicId },
          {
            projection: {
              ResearchTopicId: 1,
              Slug: 1,
              Title: 1,
              IsMagazinePage: 1
            }
          }
        );
      rt = rt2025 ? mapResearchTopicBasicInfo2025ToLegacy(rt2025) : null;
    } else {
      rt = await collection.findOne<IResearchTopicBasicInfoSchema>(
        { ResearchTopicId: researchTopicId },
        {
          projection: {
            ResearchTopicId: 1,
            Slug: 1,
            Title: 1,
            IsMagazinePage: 1
          }
        }
      );
    }

    return rt;
  };

  const getResearchTopicDetails = async (
    researchTopicId: number,
    tenantName: string
  ): Promise<IResearchTopic> => {
    const { collection, dataSource } =
      await getCollectionWithMetadata("researchTopics");

    let researchTopic: IResearchTopicSchema | null = null;

    if (dataSource === "replicator2025") {
      const rt2025 = await collection.findOne<IResearchTopic2025Schema>(
        { ResearchTopicId: researchTopicId },
        { projection: getResearchTopicProjection(dataSource) }
      );
      researchTopic = rt2025 ? mapResearchTopic2025ToLegacy(rt2025) : null;
    } else {
      researchTopic = await collection.findOne<IResearchTopicSchema>(
        { ResearchTopicId: researchTopicId },
        { projection: getResearchTopicProjection(dataSource) }
      );
    }

    if (!researchTopic) {
      throw Error(`Research Topic ID ${researchTopicId} not found in MongoDB`);
    }

    const sectionIds: number[] = [];
    researchTopic.Journals.forEach((m) => {
      if (!Array.isArray(m.Sections)) return;
      sectionIds.push(...m.Sections.map((s) => s.SectionId));
    });

    const allJournalsPromise = getJournals(
      researchTopic.Journals.map((j) => j.JournalId),
      sectionIds
    );
    const allArticlePromise = getArticles(researchTopicId);
    const ebookPromise =
      researchTopic.ClosedOn < new Date()
        ? getEbooks(researchTopicId)
        : Promise.resolve([]);

    const promises: [
      Promise<IJournalSchema[]>,
      Promise<IArticle[]>,
      Promise<IResearchTopicEbookSchema[]>
    ] = [allJournalsPromise, allArticlePromise, ebookPromise];

    const [allJournalsResponse, allArticlesResponse, ebooksResponse] =
      await Promise.all(promises);

    const allJournals: IJournalSchema[] = allJournalsResponse;
    const allArticles: IArticle[] = allArticlesResponse;
    const ebooks: string[] = ebooksResponse.map(
      (ebook) => ebook.FileFormatDescription
    );

    const journalsSchemas = allJournals.filter((j) =>
      (researchTopic?.Journals || [])
        .map((j) => j.JournalId)
        .includes(j.JournalId)
    );

    const researchTopicdata = ResearchTopicSchema2Interface(
      researchTopic,
      journalsSchemas,
      ebooks,
      tenantName
    );

    researchTopicdata.journals =
      removeOfflineJournalsAndSectionsWithoutArticles(
        researchTopicdata.journals,
        allArticles
      );

    return researchTopicdata;
  };

  const getResearchTopicSponsorshipDetails = async (
    researchTopicId: number
  ): Promise<IRtSponsorship> => {
    const { collection } = await getCollectionWithMetadata(
      "researchTopicsSponsorships"
    );
    const sponsor = await collection.findOne<IRTSponsorshipSchema>(
      { ResearchTopicId: Number(researchTopicId), IsDeleted: false },
      {
        projection: {
          Image: true,
          Message: true,
          Link: true,
          IsDeleted: true
        }
      }
    );
    return sponsor ? RTSponsorshipSchema2Interface(sponsor) : {};
  };

  return {
    getArticles,
    getAuthors,
    getResearchTopicDetails,
    getResearchTopicSponsorshipDetails,
    getResearchTopicBasicInfo,
    existsResearchTopic
  };
};
